import { z } from "zod";
/**
 * The ONE normalized shape every importer produces and the ONE shape the
 * MyManager provisioner consumes. N fragile adapters in front, one mapper behind.
 */
export const ShopHours = z.object({
    day: z.number().int().min(0).max(6), // 0 = Sunday
    open: z.string().regex(/^\d{2}:\d{2}$/),
    close: z.string().regex(/^\d{2}:\d{2}$/),
    closed: z.boolean().default(false),
});
export const ShopModifierOption = z.object({
    name: z.string().min(1),
    price: z.number().min(0).default(0),
});
export const ShopModifier = z.object({
    name: z.string().min(1),
    required: z.boolean().default(false),
    multiple: z.boolean().default(false),
    options: z.array(ShopModifierOption).default([]),
});
/**
 * A variant axis ("Size": Small / Medium / Large). Each option's price is the
 * item's FULL price at that option, not an add-on — that is how MyManager's
 * variant records (ProductNewVariant) price a combination. Modifiers, by
 * contrast, are add-ons on top of the price (extra cheese +1.50).
 */
export const ShopVariantOption = z.object({
    name: z.string().min(1),
    price: z.number().min(0).default(0),
});
export const ShopVariant = z.object({
    name: z.string().min(1),
    options: z.array(ShopVariantOption).min(1),
});
export const ShopItem = z.object({
    externalId: z.string().nullish(),
    name: z.string().min(1),
    description: z.string().default(""),
    price: z.number().min(0),
    imageUrl: z.string().url().nullish(),
    category: z.string().min(1),
    /** Add-on option sets (checkbox / radio groups with a price per option). */
    modifiers: z.array(ShopModifier).default([]),
    /** Variant axes (sizes, packs, colours); at most two. */
    variants: z.array(ShopVariant).max(2).default([]),
    available: z.boolean().default(true),
});
export const ShopCategory = z.object({
    name: z.string().min(1),
    sortOrder: z.number().int().default(0),
});
export const NormalizedShop = z.object({
    business: z.object({
        name: z.string().min(1),
        tagline: z.string().default(""),
        description: z.string().default(""),
        cuisine: z.string().default(""),
        phone: z.string().default(""),
        email: z.string().default(""),
        website: z.string().default(""),
        address: z
            .object({
            line1: z.string().default(""),
            city: z.string().default(""),
            state: z.string().default(""),
            postalCode: z.string().default(""),
            country: z.string().default("US"),
        })
            .default({}),
        logoUrl: z.string().url().nullish(),
        heroImageUrl: z.string().url().nullish(),
        brandColor: z
            .string()
            .regex(/^#[0-9a-fA-F]{6}$/)
            .optional(),
        /** Second brand colour (accent / dark tone) read from their site's CSS. */
        brandSecondaryColor: z
            .string()
            .regex(/^#[0-9a-fA-F]{6}$/)
            .optional(),
        /** Typefaces their site loads (Google Fonts family names when available). */
        headingFont: z.string().max(40).optional(),
        bodyFont: z.string().max(40).optional(),
        faviconUrl: z.string().url().nullish(),
        rating: z.number().min(0).max(5).nullish(),
        reviewCount: z.number().int().min(0).nullish(),
    }),
    hours: z.array(ShopHours).default([]),
    categories: z.array(ShopCategory).default([]),
    items: z.array(ShopItem).default([]),
    images: z.array(z.string().url()).default([]),
    /** Where this came from; kept so the review UI can show provenance per field. */
    provenance: z
        .object({ platform: z.string(), sourceUrl: z.string().nullish(), fetchedAt: z.string() })
        .nullish(),
});
/**
 * Settings the demo ShopV2 is created with. Field names mirror MyManager's
 * ShopV2 document (and PosSettings for tips) so the provisioner can `$set`
 * them by path. Everything optional: unset = MyManager's default.
 */
const flags = (keys) => z
    .object(Object.fromEntries(keys.map((k) => [k, z.boolean()])))
    .partial();
export const POS_SETTING_KEYS = [
    "enableInStore",
    "enablePickup",
    "enableDelivery",
    "enableTableSelection",
    "enableCustomerSelection",
    "closeOrderAfterPayment",
];
export const NAV_ITEM_KEYS = [
    "orders",
    "tables",
    "kds",
    "cfd",
    "reservation",
    "giftCard",
    "sellGiftCard",
    "splitPayment",
    "tips",
    "timeclock",
    "checkin",
    "memberships",
    "courses",
    "eightySix",
    "cashDrawer",
    "payCash",
    "payCard",
    "payTerminal",
    "payCheck",
    "chargeAch",
    "scanCustomer",
    "noSale",
    "payout",
    "reports",
    "transactions",
    "batches",
    "products",
    "addProduct",
    "services",
    "addEmployee",
    "mixingStation",
];
export const CHECKOUT_FLAG_KEYS = [
    "requireLogin",
    "trackOrder",
    "autoSendReceiptSms",
    "autoSendOrderConfirmationEmail",
    "giftCardsEnabled",
    "addressAutoCompletion",
    "validateShippingAddress",
];
/**
 * ShopV2.businessType — the exact values Shop+ → Settings → General offers. It
 * drives the POS mode and the Mixing Station variant (Services → Color Bar,
 * Restaurants & Bars → Pour Station). A new prospect starts on Restaurants & Bars;
 * the rep changes it after the import when the business is something else.
 */
export const SHOP_BUSINESS_TYPES = [
    "Restaurants & Bars",
    "Services",
    "Gyms",
    "Retail & General Business",
];
export const DEFAULT_SHOP_BUSINESS_TYPE = "Restaurants & Bars";
export const ShopSettings = z.object({
    businessType: z.enum(SHOP_BUSINESS_TYPES).optional(),
    timezone: z.string().optional(),
    /** Online store: pass card fees on to the customer (dual pricing / surcharge). */
    passOnCC: z.boolean().optional(),
    autoCloseOrder: z.boolean().optional(),
    showDeliveryMethodAtStart: z.boolean().optional(),
    onlineStoreDeliveryEnabled: z.boolean().optional(),
    /** ShopV2.posSettings — what the register offers. */
    posSettings: flags(POS_SETTING_KEYS).optional(),
    /** ShopV2.navItemsState — which POS screens / payment types are switched on. */
    navItemsState: flags(NAV_ITEM_KEYS).optional(),
    /** ShopV2.checkoutSettings — the online checkout. */
    checkoutSettings: flags(CHECKOUT_FLAG_KEYS)
        .extend({
        contactMethod: z.enum(["email", "phone", "both"]).optional(),
        tipping: z
            .object({
            show: z.boolean().optional(),
            tipOptions: z.array(z.number().min(0).max(100)).max(6).optional(),
        })
            .optional(),
        autoGratuity: z
            .object({
            enabled: z.boolean().optional(),
            tiers: z
                .array(z.object({ minGuests: z.number().int().min(1), percent: z.number().min(0).max(100) }))
                .max(5)
                .optional(),
        })
            .optional(),
        discountCode: z.object({ show: z.boolean().optional() }).optional(),
        preAuth: z.object({ show: z.boolean().optional(), hold: z.string().optional() }).optional(),
        futurePickups: z
            .object({
            enabled: z.boolean().optional(),
            maxOrdersPerSlot: z.number().int().min(1).max(100).optional(),
            slotDuration: z.number().int().min(5).max(120).optional(),
        })
            .optional(),
    })
        .optional(),
    /** PosSettings.configureTip*Percentage — the four tip buttons on the register. */
    posTipPercentages: z.array(z.number().min(0).max(100)).max(4).optional(),
});
export const emptyShop = (name) => NormalizedShop.parse({ business: { name } });
//# sourceMappingURL=shop.js.map